import React, { useState, useMemo, useCallback } from "react";
import { Button, Input, Text } from "@teals/ui-web";
import {
  useGetDataSourcesQuery,
  useDeleteDataSourceMutation,
  type DataSource,
  type DsType,
} from "../../store/api/dataSourcesApi";
import { DS_TYPE_LABELS } from "./constants";
import {
  PageWrapper,
  Header,
  HeaderRow,
  Subtitle,
  SearchRow,
  FilterBadge,
  Grid,
  EmptyState,
  ErrorBanner,
} from "./styles";
import DataSourceCard from "./DataSourceCard";
import DataSourceDetail from "./DataSourceDetail";
import CreateWizard from "./CreateWizard/CreateWizard";
import { API_BASE_URL } from "../../utils/api";

const DataSources: React.FC = () => {
  const { data: dataSources, isLoading, error } = useGetDataSourcesQuery();
  const [deleteDs] = useDeleteDataSourceMutation();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<DsType | null>(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedDs, setSelectedDs] = useState<DataSource | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTab, setDetailTab] = useState<"overview" | "upload">("overview");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filteredSources = useMemo(() => {
    if (!dataSources) return [];
    return dataSources.filter((ds) => {
      const matchesSearch =
        !search ||
        ds.name.toLowerCase().includes(search.toLowerCase()) ||
        ds.dataSourceId.toLowerCase().includes(search.toLowerCase());
      const matchesType = !typeFilter || ds.dsType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [dataSources, search, typeFilter]);

  const openDetail = useCallback(
    (ds: DataSource, tab: "overview" | "upload" = "overview") => {
      setSelectedDs(ds);
      setDetailTab(tab);
      setDetailOpen(true);
      setDeleteConfirm(null);
    },
    [],
  );

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedDs(null);
    setDeleteConfirm(null);
  }, []);

  const handleDelete = async (dsId: string) => {
    try {
      await deleteDs(dsId).unwrap();
      setDeleteConfirm(null);
      if (selectedDs?.dataSourceId === dsId) closeDetail();
    } catch {
      /* RTK error state handles display */
    }
  };

  return (
    <PageWrapper>
      <Header>
        <HeaderRow>
          <Text variant="h2">Data Sources</Text>
          <Button variant="primary" onClick={() => setWizardOpen(true)}>
            + Create Data Source
          </Button>
        </HeaderRow>
        <Subtitle>Configure retrieval context for your agents</Subtitle>
      </Header>

      <SearchRow>
        <Input
          placeholder="Search data sources\u2026"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 280 }}
        />
        {(Object.keys(DS_TYPE_LABELS) as DsType[]).map((t) => (
          <FilterBadge
            key={t}
            variant="info"
            size="sm"
            $active={typeFilter === t}
            onClick={() => setTypeFilter(typeFilter === t ? null : t)}
          >
            {DS_TYPE_LABELS[t]}
          </FilterBadge>
        ))}
      </SearchRow>

      {error && (
        <ErrorBanner>
          Failed to load data sources. Please try again.
        </ErrorBanner>
      )}

      {isLoading ? (
        <EmptyState>Loading data sources\u2026</EmptyState>
      ) : filteredSources.length === 0 ? (
        <EmptyState>
          {dataSources?.length === 0
            ? 'No data sources yet. Click "Add Data Source" to get started.'
            : "No data sources match your search."}
        </EmptyState>
      ) : (
        <Grid>
          {filteredSources.map((ds) => (
            <DataSourceCard
              key={ds.dataSourceId}
              ds={ds}
              onClick={() => openDetail(ds)}
            />
          ))}
        </Grid>
      )}

      <DataSourceDetail
        ds={selectedDs}
        isOpen={detailOpen}
        onClose={closeDetail}
        initialTab={detailTab}
        deleteConfirm={deleteConfirm}
        onDeleteConfirm={setDeleteConfirm}
        onDelete={handleDelete}
      />

      <CreateWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        apiBaseUrl={API_BASE_URL}
        onOpenDetail={(ds, tab) => {
          setWizardOpen(false);
          openDetail(ds, tab);
        }}
      />
    </PageWrapper>
  );
};

export default DataSources;
